import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { captureException } from '@sentry/react'
import { SpinnerWrapper } from '../../../components/common/SpinnerWrapper'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { theme } from '../../../theme/theme'
import { Button, ButtonVariant } from '../../../components/common/Button'
import { IllustrationChoices } from '../../../components/common/Illustration'
import { Todo } from '../../../components/Todo/Todo'
import { IconChoices } from '../../../components/common/Icon/Icon'
import { ErrorView } from '../../error/ErrorView'
import { reverseUrl } from '../../../navigation/reverseUrl'
import {
  societyGuidesQueries,
  CONSTANTS_SOCIETY_GUIDES,
  completeGuide,
} from '../../../api/society-guides'
import { StartGetToKnow } from './StartGetToKnow'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'
import { isUserAdminInSociety } from '../../../helpers/society'
import { useCurrentSociety } from '../../../hooks/useCurrentSociety'
import { i18nSocietyType } from '../../../i18n'

const PageDescription = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  return (
    <div>
      <h2 style={{ ...theme.textVariants.h2, marginTop: theme.spacing.m }}>
        {translate('getToKnowScreen.title')}
      </h2>

      <p style={{ ...theme.textVariants.base, marginBottom: theme.spacing.m }}>
        {translate('getToKnowScreen.description')}
      </p>
    </div>
  )
}

const TodosCompleted = ({
  onPress,
  isLoading,
}: {
  onPress: () => void
  isLoading: boolean
}): React.ReactNode => {
  const { translate } = useAppTranslation()
  return (
    <div
      style={{ marginBottom: theme.spacing.l }}
      className="rounded-md bg-brandLightBrown border border-brandBrown p-5"
    >
      <h2 style={{ ...theme.textVariants.h2 }}>
        {translate('getToKnowScreen.todosCompleted.title')}
      </h2>

      <div style={{ flexDirection: 'row' }}>
        <p style={{ ...theme.textVariants.base, flexWrap: 'wrap', flex: 1 }}>
          {translate('getToKnowScreen.todosCompleted.description')}
        </p>
      </div>
      <Button
        onClick={onPress}
        loading={isLoading}
        style={{ marginTop: theme.spacing.s }}
        variant={ButtonVariant.PRIMARY}
        label={translate('getToKnowScreen.todosCompleted.button')}
      />
    </div>
  )
}

export const GetToKnowView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { id: societyId } = useParams()
  const { society } = useCurrentSociety()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const authUserId = useAuthenticatedUserId()
  const currentUserIsAdmin = isUserAdminInSociety(society, authUserId as string)
  const { data: getToKnow, isLoading: isLoadingGetToKnow } = useQuery(
    societyGuidesQueries.getToKnow(societyId, currentUserIsAdmin)
  )

  useDocumentTitle(reverseDocumentTitle('management:get-to-know'))
  const { mutate: mutateComplete, isPending: isCompletePending } = useMutation({
    mutationFn: async () => {
      await completeGuide({
        completed: true,
        societyId: societyId!,
        guideId: CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW,
      })
      await queryClient.invalidateQueries({
        queryKey: societyGuidesQueries.getToKnow(societyId).queryKey,
      })
      navigate(
        reverseUrl('management:get-to-know-completed', { id: societyId })
      )
    },
  })
  const { mutate: mutateUncompleted, isPending: isUncompletedPending } =
    useMutation({
      mutationFn: async () => {
        await completeGuide({
          completed: false,
          societyId: societyId!,
          guideId: CONSTANTS_SOCIETY_GUIDES.GUIDE_ID.GET_TO_KNOW,
        })

        await queryClient.invalidateQueries({
          queryKey: societyGuidesQueries.getToKnow(societyId).queryKey,
        })
      },
    })

  const isLoading = isLoadingGetToKnow

  if (isLoading) {
    return <SpinnerWrapper />
  }

  if (!getToKnow?.started) {
    return <StartGetToKnow societyId={societyId!} />
  }

  if (getToKnow?.completed) {
    return (
      <ErrorView
        title=""
        subtitle={translate('getToKnowScreen.completedView.description', {
          context: i18nSocietyType(society?.societyTypeDisplayName),
        })}
        illustration={IllustrationChoices.CHECKLIST}
        buttons={[
          {
            id: 'get-to-know-undo-button',
            button: {
              label: translate('getToKnowScreen.completedView.button'),
              variant: ButtonVariant.PRIMARY,
              loading: isUncompletedPending,
              onClick: () => {
                mutateUncompleted()
              },
            },
          },
        ]}
      />
    )
  }

  const todos: React.ReactNode[] | undefined = getToKnow?.steps
    ?.map((step, index) => {
      switch (step.id) {
        case 'board': {
          return (
            <Link
              key={step.id}
              to={reverseUrl('management:register-member', { id: societyId })}
            >
              <Todo.Item
                index={index}
                title={translate('getToKnowScreen.todos.board.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                description={translate(
                  'getToKnowScreen.todos.board.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                done={step.completed}
                icon={IconChoices.BOARD}
              />
            </Link>
          )
        }
        case 'feed': {
          return (
            <Link key={step.id} to={reverseUrl('feed')}>
              <Todo.Item
                index={index}
                title={translate('getToKnowScreen.todos.feed.title')}
                description={translate(
                  'getToKnowScreen.todos.feed.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                done={step.completed}
                icon={IconChoices.FEED}
              />
            </Link>
          )
        }
        case 'chat': {
          return (
            <Link key={step.id} to={reverseUrl('chat')}>
              <Todo.Item
                index={index}
                title={translate('getToKnowScreen.todos.chat.title', {
                  context: i18nSocietyType(society?.societyTypeDisplayName),
                })}
                description={translate(
                  'getToKnowScreen.todos.chat.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                done={step.completed}
                icon={IconChoices.CHAT}
              />
            </Link>
          )
        }
        case 'calendar': {
          return (
            <Link
              key={step.id}
              to={reverseUrl('management:calendar', { id: societyId })}
            >
              <Todo.Item
                index={index}
                title={translate('getToKnowScreen.todos.calendar.title')}
                description={translate(
                  'getToKnowScreen.todos.calendar.description',
                  { context: i18nSocietyType(society?.societyTypeDisplayName) }
                )}
                done={step.completed}
                icon={IconChoices.CALENDAR}
              />
            </Link>
          )
        }
        default: {
          captureException(`GetToKnowView: Unknown get to know id ${step.id}`)
          return <></>
        }
      }
    })
    .flatMap((todo) => (todo ? [todo] : []))

  if (!todos) {
    return null
  }

  const stepsDone = getToKnow?.steps?.filter((t) => t.completed).length || 0
  const total = getToKnow?.steps?.length || 0
  return (
    <div
      className="max-w-2xl"
      style={{
        backgroundColor: theme.colors.white,
      }}
    >
      {total === stepsDone && (
        <TodosCompleted
          isLoading={isCompletePending}
          onPress={() => mutateComplete()}
        />
      )}
      <PageDescription />
      <Todo.Progress stepsDone={stepsDone} totalSteps={total} />
      <div style={{ marginTop: theme.spacing.m }}>{todos}</div>
    </div>
  )
}
